import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FiberManualRecord } from '@material-ui/icons'
import cn from 'classnames'
import { Popover, Typography, IconButton } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {
  LEVEL_ALERT,
  LEVEL_NORMAL,
  LEVEL_WARNING,
  LEVEL_NO_STATUS,
} from 'constants/indicators'
import { formatMessage } from 'lib/intl'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import { ReactComponent as InfoIcon } from 'assets/img/Info-icon.svg'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import SidePopover from 'components/common/Popover/SidePopover'
import Indicator from 'routes/App/Apps/MonitorsAndAlerts/Content/Table/Indicators'

function JobTaskPolicies({ locus, needsLocus, policyIndicators }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const isMobileView = ifWindowWidthLessThan(TABLET_BREAKPOINT)
  const isInfoOpen = Boolean(anchorEl)

  if (!policyIndicators.length) {
    return null
  }

  const handlePopoverOpen = ({ currentTarget }) => {
    setAnchorEl(currentTarget)
  }
  const handlePopoverClose = () => {
    setAnchorEl(null)
  }
  const getIndicatorValue = value => {
    let result = 'N/A'

    if (value || value === 0) {
      result = value

      if (typeof value === 'string' && value.includes('_')) {
        result = formatMessage(value)
      }
    }

    return result
  }
  const getIndicatorPosition = indicatorStatus => {
    const indicatorsWeights = {
      [LEVEL_ALERT]: 3,
      [LEVEL_WARNING]: 2,
      [LEVEL_NORMAL]: 1,
      [LEVEL_NO_STATUS]: 0,
    }

    return indicatorsWeights[indicatorStatus]
  }
  const getIndicatorDetailsContent = indicators => (
    <>
      <div className="indicator-details-header">
        <Typography className="indicator-details-title">
          {formatMessage('indicator_details')}
        </Typography>
        {isMobileView && (
          <IconButton size="small" onClick={handlePopoverClose}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
      <div className="indicator-details-list">
        {indicators.map(item => (
          <div key={item.policy} className="indicator-details-item">
            <Typography className="details-item-name">{item.policy}</Typography>
            {item.details && (
              <Typography className="details-item-description">
                {item.details}
              </Typography>
            )}
            <Indicator {...item} className="jobs-indicator" />
          </div>
        ))}
      </div>
    </>
  )

  const indicators = policyIndicators
    .reduce((result, { indicators, policy: { name, ...restPolicyData } }) => {
      const total = [...result]
      const noDataPattern = {
        position: getIndicatorPosition(LEVEL_NO_STATUS),
        status: LEVEL_NO_STATUS,
        policy: name,
        value: 'N/A',
      }

      if (indicators.length && indicators.length === 1) {
        const { criticality_level, value, ...restIndicatorData } = indicators[0]

        total.push({
          position: getIndicatorPosition(criticality_level),
          status: criticality_level,
          policy: name,
          value: getIndicatorValue(value),
          ...restIndicatorData,
          ...restPolicyData,
        })
      } else if (indicators.length > 1 && needsLocus && locus) {
        const foundIndicator = indicators.find(item => item.locus === locus)

        if (foundIndicator && Object.keys(foundIndicator).length) {
          const {
            criticality_level: criticalityLevelByLocus,
            value: valueByLocus,
            ...restFoundIndicatorData
          } = foundIndicator

          total.push({
            position: getIndicatorPosition(criticalityLevelByLocus),
            status: criticalityLevelByLocus,
            policy: name,
            value: getIndicatorValue(valueByLocus),
            ...restFoundIndicatorData,
            ...restPolicyData,
          })
        } else {
          total.push(noDataPattern)
        }
      } else {
        total.push(noDataPattern)
      }

      return total
    }, [])
    .sort((a, b) => b.position - a.position)
  const detailedIndicators = indicators.filter(
    ({ indicator_health_bar }) => indicator_health_bar,
  )

  return (
    <div className="job-indicator-list">
      {indicators.map(({ status, policy, value }) => (
        <div key={policy} className="indicator">
          <FiberManualRecord
            className={cn('indicator-status', { [status]: status })}
          />
          <div className="indicator-name">{policy}</div>
          <div className="indicator-value">{value}</div>
        </div>
      ))}
      {!!detailedIndicators.length && (
        <IconButton
          disableRipple
          disableFocusRipple
          size="small"
          className="icon-info"
          onClick={handlePopoverOpen}>
          <InfoIcon />
        </IconButton>
      )}
      {isMobileView ? (
        <SidePopover
          open={isInfoOpen}
          close={handlePopoverClose}
          position="bottom"
          classes={{ paper: 'indicator-details-mobile' }}>
          {getIndicatorDetailsContent(detailedIndicators)}
        </SidePopover>
      ) : (
        <Popover
          open={isInfoOpen}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          classes={{ paper: 'indicator-details-popover' }}
          className="indicator-details-view"
          onClose={handlePopoverClose}>
          {getIndicatorDetailsContent(detailedIndicators)}
        </Popover>
      )}
    </div>
  )
}

JobTaskPolicies.propTypes = {
  locus: PropTypes.number,
  needsLocus: PropTypes.bool,
  policyIndicators: PropTypes.array,
}

JobTaskPolicies.defaultProps = {
  locus: null,
  needsLocus: false,
  policyIndicators: [],
}

export default JobTaskPolicies
